import {type V2PersonalBest, V2PersonalBestSchema} from "@repo/shared";
import {serverTimestamp} from "firebase/firestore";

import {BaseCollectionConfig} from "../BaseCollectionConfig";
import {collectionNameV2} from "../collectionName";
import {getByIdHookFactory} from "../factories/getByIdHookFactory";
import {getByIdRealtimeSubFactory} from "../factories/getByIdRealtimeSubFactory";
import {lazyCallbackFactory} from "../factories/lazyCallbackFactory";
import {mutationHookFactory} from "../factories/mutationHookFactory";
import {queryHookFactory} from "../factories/queryHookFactory";
import {queryRealtimeHookFactory} from "../factories/queryRealtimeHookFactory";

export class PersonalBestV2CollectionConfig extends BaseCollectionConfig<
  typeof V2PersonalBestSchema,
  {levelId?: string}
> {
  constructor() {
    super(V2PersonalBestSchema);
  }

  getCollection({
    levelId,
  }: {
    levelId?: string;
  }): [string, ...(string | undefined)[]] {
    return [collectionNameV2("levels"), levelId, "V2-personal-best"];
  }

  parser(id: string, data: any): V2PersonalBest {
    if (data === undefined) {
      // Return default values if no data exists
      return {
        userId: id,
        levelId: "",
        replay: null,
        stats: {
          totalReplays: 0,
          completions: 0,
          attemptsUntilFirstCompletion: null,
          averageAttemptsForCompletions: 0,
          totalTimeSpent: 0,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }

    return {
      userId: id,
      levelId: data.levelId || "",
      replay: data.replay || null,
      stats: {
        totalReplays: data.stats?.totalReplays || 0,
        completions: data.stats?.completions || 0,
        attemptsUntilFirstCompletion:
          data.stats?.attemptsUntilFirstCompletion || null,
        averageAttemptsForCompletions:
          data.stats?.averageAttemptsForCompletions || 0,
        totalTimeSpent: data.stats?.totalTimeSpent || 0,
      },
      createdAt: data.createdAt || new Date(),
      updatedAt: data.updatedAt || new Date(),
    };
  }

  mapForPersistence(data: V2PersonalBest): V2PersonalBest {
    return {
      ...data,
      updatedAt: serverTimestamp() as any,
    };
  }
}

const config = new PersonalBestV2CollectionConfig();
export const usePersonalBestV2 = getByIdHookFactory(config);
export const usePersonalBestV2RealtimeSub = getByIdRealtimeSubFactory(config);
export const useUpdatePersonalBestV2 = mutationHookFactory(config);
export const useLazyCallbackPersonalBestV2 = lazyCallbackFactory(config);
export const useRealtimePersonalBestV2Query = queryRealtimeHookFactory(config);
export const usePersonalBestV2Query = queryHookFactory(config);
